/**
 * Resolves the ERP design-system color tokens (the same `--chart-1..5`,
 * `--foreground`, `--border`, … custom properties defined in `@erp/ui`) into
 * concrete color strings that Vega can render.
 *
 * In the browser we read the *computed* values straight off the DOM, so charts
 * automatically match the active theme (light/dark) and any per-org token
 * overrides. When there is no DOM (SSR, tests, pure spec building) we fall back
 * to the literal sRGB values of the shipped tokens.
 */

export interface VizPalette {
  /** Page/card background — charts render transparent on top of this. */
  background: string;
  /** Primary text color, used for titles and axis titles. */
  foreground: string;
  /** Secondary text color, used for axis/legend labels. */
  mutedForeground: string;
  /** Hairline color, used for gridlines, ticks and axis domains. */
  border: string;
  /** Brand color — default mark color for single-series charts. */
  primary: string;
  /** Categorical color scheme (`--chart-1` … `--chart-5`). */
  categorical: string[];
}

/** CSS custom properties backing each palette slot. */
const TOKENS = {
  background: "--background",
  foreground: "--foreground",
  mutedForeground: "--muted-foreground",
  border: "--border",
  primary: "--primary",
} as const;

const CATEGORICAL_TOKENS = [
  "--chart-1",
  "--chart-2",
  "--chart-3",
  "--chart-4",
  "--chart-5",
] as const;

/**
 * sRGB fallbacks, computed from the oklch token values in
 * `packages/ui/src/styles.css`. Only used when the DOM is unavailable; in the
 * browser the live computed values take precedence.
 */
export const FALLBACK_LIGHT: VizPalette = {
  background: "#ffffff",
  foreground: "#0d0b0a",
  mutedForeground: "#79716b",
  border: "#e7e5e4",
  primary: "#1c1917",
  categorical: ["#f54900", "#009689", "#104e64", "#ffb900", "#fe9a00"],
};

export const FALLBACK_DARK: VizPalette = {
  background: "#0d0b0a",
  foreground: "#fafaf9",
  mutedForeground: "#a6a09b",
  border: "rgba(255, 255, 255, 0.1)",
  primary: "#e7e5e4",
  categorical: ["#1447e6", "#00bc7d", "#fe9a00", "#ad46ff", "#ff2056"],
};

function hasDom(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof document !== "undefined" &&
    !!document.body
  );
}

/**
 * Normalizes any CSS color string (oklch, hsl, named, hex, …) into a value Vega
 * can paint in both the SVG and canvas renderers, by letting the browser
 * resolve it to its computed `rgb()` / `rgba()` form.
 */
function normalizeColor(value: string, root: HTMLElement): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  try {
    const probe = document.createElement("span");
    probe.style.color = trimmed;
    probe.style.display = "none";
    root.appendChild(probe);
    const computed = getComputedStyle(probe).color;
    root.removeChild(probe);
    return computed || trimmed;
  } catch {
    return trimmed;
  }
}

function readToken(
  root: HTMLElement,
  styles: CSSStyleDeclaration,
  token: string,
  fallback: string,
): string {
  const raw = styles.getPropertyValue(token);
  if (!raw || !raw.trim()) return fallback;
  const normalized = normalizeColor(raw, root);
  return normalized || fallback;
}

/** Whether the active theme is dark, derived from the `.dark` class. */
export function detectDark(root?: HTMLElement): boolean {
  if (!hasDom()) return false;
  const target = root ?? document.documentElement;
  return (
    target.classList.contains("dark") ||
    document.documentElement.classList.contains("dark") ||
    !!target.closest(".dark")
  );
}

/**
 * Resolves the full chart palette from the DOM, falling back to the shipped
 * sRGB token values when no DOM is available.
 */
export function resolvePalette(
  root?: HTMLElement,
  isDark?: boolean,
): VizPalette {
  const dark = isDark ?? detectDark(root);
  if (!hasDom()) return dark ? FALLBACK_DARK : FALLBACK_LIGHT;

  const target = root ?? document.documentElement;
  const styles = getComputedStyle(target);
  const fallback = dark ? FALLBACK_DARK : FALLBACK_LIGHT;

  return {
    background: readToken(
      target,
      styles,
      TOKENS.background,
      fallback.background,
    ),
    foreground: readToken(
      target,
      styles,
      TOKENS.foreground,
      fallback.foreground,
    ),
    mutedForeground: readToken(
      target,
      styles,
      TOKENS.mutedForeground,
      fallback.mutedForeground,
    ),
    border: readToken(target, styles, TOKENS.border, fallback.border),
    primary: readToken(target, styles, TOKENS.primary, fallback.primary),
    categorical: CATEGORICAL_TOKENS.map((token, i) =>
      readToken(target, styles, token, fallback.categorical[i]),
    ),
  };
}
